import axios from 'axios';
import { notificationService } from './notificationService';

// API Base URL configuration
// For local development, this typically matches REACT_APP_API_URL defined in .env
// For production, it matches REACT_APP_API_URL defined in .env.production
export const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://api.jitinfotech.com/api/v1';

// Create axios instance with default config
const api = axios.create({
    baseURL: API_BASE_URL,
    withCredentials: true,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Request interceptor
api.interceptors.request.use(
    (config) => {
        console.log(`Making ${config.method?.toUpperCase()} request to ${config.url}`);
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Response interceptor
api.interceptors.response.use(
    (response) => {
        return response;
    },
    (error) => {
        console.error('API Error:', error.response?.data || error.message);

        if (error.response) {
            if (error.response.status === 422) {
                const validationErrors = error.response.data?.errors || {};
                return Promise.reject(validationErrors);
            }

            const isUnauthorized = error.response.status === 401;
            const isForbidden = error.response.status === 403;
            const errorData = error.response.data || {};

            // Check if we already have a user. If not, we don't need to auto-logout/alert
            const userExists = localStorage.getItem('user');

            // Don't auto-logout if we're on the login page or it's a login attempt
            const isLoginRequest = error.config?.url?.includes('/jit/auth/login');

            if ((isUnauthorized || isForbidden) && userExists && !isLoginRequest) {
                // Remove user first to prevent multiple alerts from concurrent failed requests
                localStorage.removeItem('user');

                alert('Your session has expired. Please log in again.');
                window.location.href = '/';
                return new Promise(() => { }); // Stop further processing
            }

            // Show backend error messages globally
            // Backend APIResponse format: { message: string, success: boolean, statusCode: number }
            if (errorData.message) {
                notificationService.showError(errorData.message);
            } else if (error.response.status === 400) {
                notificationService.showError('Invalid request. Please check your input.');
            } else if (error.response.status === 409) {
                notificationService.showError('Conflict: Resource already exists or operation cannot be completed.');
            } else if (error.response.status === 500) {
                notificationService.showError('Server error. Please try again later.');
            } else {
                notificationService.showError(`Error: ${error.message}`);
            }
        } else if (error.message) {
            notificationService.showError(`Network Error: ${error.message}`);
        }
        return Promise.reject(error);
    }
);

// Laptop API functions
export const laptopAPI = {
    // Get all laptops with optional filters
    getLaptops: (params = {}) => {
        return api.get('/laptops', { params });
    },

    // Get laptop statistics
    getLaptopStats: () => {
        return api.get('/laptops/stats');
    },

    getTotalProfit: () => {
        return api.get('/jit/sales-orders/report/total-net-profit');
    },

    getPendingOrders: () => {
        return api.get('/jit/sales-orders/report/pending-orders/excel', {
            responseType: 'blob'
        });
    },


    // Create new laptop
    createProduct: (laptopData) => {
        return api.post('/jit/products', laptopData);
    },

    // Update laptop
    updateLaptop: (id, laptopData) => {
        return api.put(`/jit/products/${id}`, laptopData);
    },

    // Update laptop stock
    updateLaptopStock: (id, stock, skuProductCodes = null) => {
        const payload = { stockQty: stock };
        if (skuProductCodes) {
            payload.skuProductCodes = skuProductCodes;
        }
        return api.patch(`/jit/products/${id}`, payload);
    },

    // Delete laptop
    deleteLaptop: (id) => {
        return api.delete(`/jit/products/${id}`);
    },
};

// Bill/Sales Order API functions
export const billAPI = {
    // Get all bills/sales orders
    getBills: (params = {}) => {
        return api.get('/jit/sales-orders', { params });
    },

    // Get bills by payment status
    getBillsByPaymentStatus: (paymentStatus, params = {}) => {
        return api.get(`/jit/sales-orders/by-payment-status/${paymentStatus}`, { params });
    },

    // Create new bill/sales order
    createBill: (billData, file = null) => {
        const formData = new FormData();
        formData.append('salesOrder', new Blob([JSON.stringify(billData)], { type: 'application/json' }));

        if (file) {
            formData.append('physicalBillFile', file);
        }

        return api.post('/jit/sales-orders', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
    },

    // Get bill by ID
    getBill: (id) => {
        return api.get(`/jit/sales-orders/${id}`);
    },

    // Update bill
    updateBill: (id, billData) => {
        return api.patch(`/jit/sales-orders/${id}`, billData);
    },

    // Upload Physical Bill
    uploadPhysicalBill: (id, file, billNumber = '') => {
        const formData = new FormData();
        formData.append('physicalBillFile', file);
        if (billNumber) {
            formData.append('billNumber', billNumber);
        }
        return api.post(`/jit/sales-orders/${id}/bill-upload`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
    },

    // Delete bill
    deleteBill: (id) => {
        return api.delete(`/jit/sales-orders/${id}`);
    },
    holdOrder: (id) => {
        return api.post(`/jit/sales-orders/hold/${id}`);
    },
    returnOrder: (id, reason) => {
        return api.patch(`/jit/sales-orders/${id}/return?reason=${encodeURIComponent(reason)}`);
    },
    checkWarrantyStatus: (sku) => {
        return api.get(`/jit/sales-orders/warranty-status/sku/${sku}`);
    },
    getPaymentHistory: (orderId) => {
        return api.get(`/jit/sales-orders/payment-history/${orderId}`);
    },
    getFinancialDetails: (id) => {
        return api.get(`/jit/sales-orders/${id}/financial-details`);
    },
    getAdminProfitStats: (params = {}) => {
        return api.get('/jit/sales-orders/report/admin-profit-stats', { params });
    },

    // Generate PDF invoice
    generatePDF: (id, includeGst = true) => {
        return api.get(`/jit/sales-orders/${id}/invoice-download`, {
            params: { includeGst },
            responseType: 'blob'
        });
    },

    // Download physical bill
    downloadPhysicalBill: (id) => {
        return api.get(`/jit/sales-orders/${id}/bill-download`, {
            responseType: 'blob'
        });
    },

    // Get all pending payments/users (Paginated)
    getPendingPayments: (params = {}) => {
        return api.get('/jit/sales-orders/report/pending-payments/users', { params });
    },

    // Get available SKU codes for a product
    getAvailableSkuCodes: (productCode) => {
        return api.get(`/jit/sales-order-items/sku-product-codes/${encodeURIComponent(productCode)}`);
    }
};

// Inventory/Product API functions
export const inventoryAPI = {
    // Get all categories
    getCategories: (params = {}) => {
        return api.get('/jit/categories/getAll', { params });
    },

    // Get products by category (or all)
    getProducts: (params = {}) => {
        const queryParams = {
            pageNumber: params.page || 0,
            pageSize: params.limit || 1000,
            sortBy: params.sortBy || 'productId',
            sortOrder: params.sortOrder || 'ASC',
            search: params.search || undefined,
            category: params.category || undefined // Passing category just in case backend accepts it; otherwise we might get all products
        };
        return api.get('/jit/products', { params: queryParams });
    },

    // Get product details by product name
    getProductDetailsByName: (productName) => {
        return api.get(`/jit/products/details/${encodeURIComponent(productName)}`);
    },

    // Create new product
    createProduct: (productData) => {
        return api.post('/products', productData);
    },

    // Update product
    updateProduct: (id, productData) => {
        return api.patch(`/jit/products/${id}`, productData);
    },

    // Delete product
    deleteProduct: (id) => {
        return api.delete(`/products/${id}`);
    },
    createCategory: (categoryData) => {
        return api.post('/jit/categories/create', categoryData);
    },
    updateCategory: (id, categoryData) => {
        // Backend uses @RequestParam for categoryName update instead of Request body
        return api.put(`/jit/categories/update/${id}`, null, { params: { categoryName: categoryData.categoryName } });
    },
    deleteCategory: (id) => {
        return api.delete(`/jit/categories/delete/${id}`);
    },
    getStockSummary: () => {
        return api.get('/jit/products/stock-summary');
    },
    // Live search products by name (for quotation item picker)
    searchProducts: (query) => {
        return api.get('/jit/products', {
            params: {
                pageNumber: 0,
                pageSize: 20,
                sortBy: 'productName',
                sortOrder: 'ASC',
                search: query
            }
        });
    },
    bulkUploadProducts: (file) => {
        const formData = new FormData();
        formData.append('file', file);
        return api.post('/jit/products/bulk-upload', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
    }
};

// Dealer Bill API functions
export const dealerAPI = {
    // List all bills for a product
    getBills: (productId) => {
        return api.get(`/jit/products/${productId}/bills`);
    },

    // Upload dealer bill
    uploadBill: (productId, file, dealerTotalBillAmount, jitPaidAmount) => {
        const formData = new FormData();
        formData.append('file', file);
        if (dealerTotalBillAmount) formData.append('dealerTotalBillAmount', dealerTotalBillAmount);
        if (jitPaidAmount) formData.append('jitPaidAmount', jitPaidAmount);

        return api.post(`/jit/products/${productId}/bills`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
    },

    // Download dealer bill
    downloadBill: (productId, billId) => {
        return api.get(`/jit/products/${productId}/bills/${billId}`, {
            responseType: 'blob'
        });
    },

    // Delete dealer bill
    deleteBill: (productId, billId) => {
        return api.delete(`/jit/products/${productId}/bills/${billId}`);
    },

    // Update dealer bill amounts
    updateBill: (productId, billId, dealerTotalBillAmount, jitPaidAmount) => {
        const params = {};
        if (dealerTotalBillAmount !== undefined && dealerTotalBillAmount !== null) params.dealerTotalBillAmount = dealerTotalBillAmount;
        if (jitPaidAmount !== undefined && jitPaidAmount !== null) params.jitPaidAmount = jitPaidAmount;

        return api.patch(`/jit/products/${productId}/bills/${billId}`, null, { params });
    }
};

// Customer API functions
export const customerAPI = {
    // Get customer by ID
    getCustomerById: (id) => {
        return api.get(`/jit/customers/${id}`);
    },
    // Search customer by phone
    searchCustomer: (phone) => {
        return api.get(`/jit/customers/search`, { params: { phone } });
    },
    // Create new customer
    createCustomer: (customerData) => {
        return api.post('/jit/customers', customerData);
    },
    // Update customer
    updateCustomer: (id, customerData) => {
        return api.put(`/jit/customers/${id}`, customerData);
    },
    // Patch customer
    patchCustomer: (id, customerData) => {
        return api.patch(`/jit/customers/${id}`, customerData);
    },
    // Get all customers
    getCustomers: (params = {}) => {
        return api.get('/jit/customers', { params });
    },
    // Delete customer
    deleteCustomer: (id) => {
        return api.delete(`/jit/customers/${id}`);
    }
};





// Health check
export const healthCheck = () => {
    return api.get('/health');
};

// Auth API functions
export const authAPI = {
    login: (loginData) => {
        return api.post('/jit/auth/login', loginData);
    },
    logout: () => {
        return api.post('/jit/auth/logout');
    },
    signup: (signupData) => {
        return api.post('/jit/auth/signup', signupData);
    },
    assignRole: (employeeId, roleIds) => {
        return api.post(`/jit/auth/assign-role/${employeeId}`, roleIds);
    },
    updatePassword: (username, oldPassword, password) => {
        return api.patch(`/jit/auth/update-password/${username}`, null, {
            params: { oldPassword, password }
        });
    },
    getCurrentUser: () => {
        return api.get('/jit/auth/me');
    }
};

// Employee API functions
export const employeeAPI = {
    getAllEmployees: (params = {}) => {
        return api.get('/jit/employees', { params });
    },
    getEmployeeById: (id) => {
        return api.get(`/jit/employees/${id}`);
    }
};

// Role API functions
export const roleAPI = {
    getAllRoles: (params = {}) => {
        return api.get('/jit/roles', { params });
    }
};

// Quotation API functions
export const quotationAPI = {
    getQuotations: (params = {}) => {
        return api.get('/jit/quotations', { params });
    },
    getQuotation: (id) => {
        return api.get(`/jit/quotations/${id}`);
    },
    createQuotation: (quotationData) => {
        return api.post('/jit/quotations', quotationData);
    },
    updateQuotation: (id, quotationData) => {
        return api.put(`/jit/quotations/${id}`, quotationData);
    },
    downloadPDF: (id) => {
        return api.get(`/jit/quotations/${id}/download`, {
            responseType: 'blob'
        });
    },
    deleteQuotation: (id) => {
        return api.delete(`/jit/quotations/${id}`);
    }
};

// Actuator API functions
// Actuator API functions (Proxied via Nginx)
const ACTUATOR_BASE_URL = '/actuator';

// Create dedicated axios instance for Actuator with credentials enabled
const actuatorClient = axios.create({
    baseURL: ACTUATOR_BASE_URL,
    withCredentials: true
});

export const actuatorAPI = {
    getHealth: () => {
        return actuatorClient.get('/health');
    },
    getMetrics: () => {
        return actuatorClient.get('/metrics');
    },
    getMetricDetails: (metricName) => {
        return actuatorClient.get(`/metrics/${metricName}`);
    },
    getLoggers: () => {
        return actuatorClient.get('/loggers');
    },
    setLoggerLevel: (loggerName, level) => {
        return actuatorClient.post(`/loggers/${loggerName}`, { configuredLevel: level }, {
            headers: { 'Content-Type': 'application/json' }
        });
    },
    getInfo: () => {
        return actuatorClient.get('/info');
    },
    getHttpExchanges: () => {
        return actuatorClient.get('/httpexchanges');
    },
    getBeans: () => {
        return actuatorClient.get('/beans');
    },
    getEnv: () => {
        return actuatorClient.get('/env');
    },
    getConfigProps: () => {
        return actuatorClient.get('/configprops');
    },
    // View Log File Content
    getLogFile: () => {
        return actuatorClient.get('/logfile', { responseType: 'text' });
    },
    downloadHeapDump: () => {
        return actuatorClient.get('/heapdump', {
            responseType: 'blob',
            timeout: 300000 // 5 minutes
        });
    },
    downloadThreadDump: () => {
        return actuatorClient.get('/threaddump', {
            responseType: 'text',
            timeout: 300000 // 5 minutes
        });
    }
};

// Feedback API
export const feedbackAPI = {
    // Public
    submitFeedback: (feedbackData) => {
        return api.post('/jit/feedback/submit', feedbackData);
    },
    getApprovedFeedback: (params = {}) => {
        return api.get('/jit/feedback/list', { params });
    },
    updateFeedback: (id, token, feedbackData) => {
        return api.put(`/jit/feedback/update/${id}`, feedbackData, { params: { publicToken: token } });
    },
    deleteFeedback: (id, token) => {
        return api.delete(`/jit/feedback/delete/${id}`, { params: { publicToken: token } });
    },

    // Admin
    getAllFeedback: (params = {}) => {
        return api.get('/jit/admin/feedback/all', { params });
    },
    approveFeedback: (id) => {
        return api.patch(`/jit/admin/feedback/approve/${id}`);
    },
    updateFeedbackAdmin: (id, feedbackData) => {
        return api.put(`/jit/admin/feedback/update/${id}`, feedbackData);
    },
    deleteFeedbackAdmin: (id) => {
        return api.delete(`/jit/admin/feedback/delete/${id}`);
    }
};

// YouTube Video API
export const videoAPI = {
    getAllVideos: () => {
        return api.get('/jit/youtube/public/videos');
    },
    addVideo: (url, title, description) => {
        return api.post('/jit/youtube/admin/videos', { url, title, description });
    },
    deleteVideo: (id) => {
        return api.delete(`/jit/youtube/admin/videos/${id}`);
    }
};

export default api;

